'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { MicrocopyItem } from '@lib/types/cms/MicrocopySet';
import { getMicrocopyValue } from '@lib/util/microcopy';
import { useCallback } from 'react';
import { Pagination } from 'ui-components';
import { useSearchControllers } from '../headless/SearchDefinitionContext';
import { PagerProps } from './Pager.types';

export const Pager = ({ className }: PagerProps) => {
  const { paginationMicrocopy: paginationMicrocopySet } = useMicrocopy<{
    paginationMicrocopy: MicrocopyItem[];
  }>();

  const controllers = useSearchControllers();
  const { state, methods } = controllers.usePager();

  // Fully inert until hydration delivers the controller methods: scrolling
  // first and no-oping the page change would jump the viewport with nothing
  // happening.
  const handlePageChange = useCallback(
    (page: number) => {
      if (!methods) return;
      window.scrollTo({ top: 0 });
      methods.selectPage(page);
    },
    [methods]
  );

  const microcopy = {
    paginationAriaLabel: getMicrocopyValue(
      paginationMicrocopySet,
      'pagination.pagination-aria-label',
      'Pagination'
    ),
    previousAriaLabel: getMicrocopyValue(
      paginationMicrocopySet,
      'pagination.previous-aria-label',
      'Previous'
    ),
    nextAriaLabel: getMicrocopyValue(
      paginationMicrocopySet,
      'pagination.next-aria-label',
      'Next'
    ),
    pageAriaLabel: getMicrocopyValue(
      paginationMicrocopySet,
      'pagination.page-aria-label',
      'Page'
    ),
    firstAriaLabel: getMicrocopyValue(
      paginationMicrocopySet,
      'pagination.first-aria-label',
      'First'
    ),
    lastAriaLabel: getMicrocopyValue(
      paginationMicrocopySet,
      'pagination.last-aria-label',
      'Last'
    ),
  };

  return (
    // Disabled until hydration delivers the controller methods — including
    // when hydration fails and methods never arrives (SearchEngineProvider
    // keeps the static controllers) — matching the Sort and ResultsPerPage
    // dropdowns instead of rendering actionable-looking buttons that no-op.
    <Pagination
      disabled={!methods}
      currentPage={state.currentPage}
      totalPages={state.maxPage}
      onPageChange={handlePageChange}
      className={className}
      microcopy={microcopy}
    />
  );
};
